import { ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/core/ui';
import { createBlock } from '../../domain/blocks';
import {
  addBlock as addBlockToDoc,
  addGroup,
  deleteBlock,
  deleteGroup,
  findGroup,
  locateBlock,
  removeEdges,
} from '../../domain/document';
import { takenIds } from '../../domain/ids';
import { indexIssues, type IssueTarget } from '../../domain/issues';
import type { BlockType, FlowDoc, FlowIssue } from '../../domain/types';
import { TestPanel } from '../test/TestPanel';
import { Canvas } from './Canvas';
import {
  BuilderContext,
  selectedBlockId,
  selectedEdgeId,
  selectedGroupId,
  type Builder,
  type Selection,
} from './context';
import { Palette } from './Palette';
import { SidePanel, type PanelTab } from './SidePanel';

/** Um pedido de teste: começa no card de início (ou no `startGroupId`). `run` reinicia. */
export interface TestRequest {
  startGroupId?: string;
  run: number;
}

interface Props {
  doc: FlowDoc;
  flowName: string;
  update: (fn: (doc: FlowDoc) => FlowDoc) => void;
  issues: FlowIssue[];
  onCheck: () => void;
  checking: boolean;
  panel: PanelTab;
  onPanel: (tab: PanelTab) => void;
  test: TestRequest | null;
  onTest: (req: TestRequest | null) => void;
}

/** A aba "Fluxo": paleta, canvas, painel de edição e o teste por cima. */
export function FlowEditor(props: Props) {
  return (
    <ReactFlowProvider>
      <EditorInner {...props} />
    </ReactFlowProvider>
  );
}

const isTyping = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));

/** A tecla vale para o canvas: foco nele (ou em lugar nenhum), nunca num campo ou botão do painel. */
const onCanvas = (t: EventTarget | null, canvas: HTMLElement | null) =>
  !isTyping(t) && (t === document.body || (t instanceof Node && !!canvas?.contains(t)));

function EditorInner({
  doc,
  flowName,
  update,
  issues,
  onCheck,
  checking,
  panel,
  onPanel,
  test,
  onTest,
}: Props) {
  const [picked, setPicked] = useState<Selection>(null);
  const flow = useReactFlow();
  const wrapper = useRef<HTMLDivElement>(null);
  const index = useMemo(() => indexIssues(issues, doc), [issues, doc]);

  // Seleção de algo que não existe mais (apagado, desfeito) vale como nada selecionado.
  const selection: Selection = useMemo(() => {
    if (!picked) return null;
    if ('edgeId' in picked) return doc.edges.some((e) => e.id === picked.edgeId) ? picked : null;
    if (!findGroup(doc, picked.groupId)) return null;
    if (picked.blockId && locateBlock(doc, picked.blockId)?.group.id !== picked.groupId)
      return { groupId: picked.groupId };
    return picked;
  }, [picked, doc]);

  const select = useCallback(
    (s: Selection) => {
      setPicked(s);
      if (s) onPanel('editar');
    },
    [onPanel],
  );

  /** O centro da área visível do canvas, em coordenadas do fluxo. */
  const visibleCenter = useCallback(() => {
    const r = wrapper.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return flow.screenToFlowPosition({ x: r.left + r.width / 2 - 140, y: r.top + r.height / 3 });
  }, [flow]);

  const addBlock = useCallback(
    (groupId: string, type: BlockType, at?: number) => {
      const r = addBlockToDoc(doc, groupId, type, at);
      update(() => r.doc);
      select({ groupId, blockId: r.blockId });
    },
    [doc, update, select],
  );

  /** Paleta: no card selecionado (depois do bloco selecionado) ou num card novo no meio da tela. */
  const addFromPalette = (type: BlockType) => {
    const groupId = selectedGroupId(selection);
    if (groupId) {
      const blockId = selectedBlockId(selection);
      const at = blockId ? locateBlock(doc, blockId) : null;
      addBlock(groupId, type, at ? at.index + 1 : undefined);
      return;
    }
    const block = createBlock(type, takenIds(doc));
    const r = addGroup(doc, visibleCenter(), [block]);
    update(() => r.doc);
    select({ groupId: r.groupId, blockId: block.id });
  };

  const addCard = () => {
    const r = addGroup(doc, visibleCenter());
    update(() => r.doc);
    select({ groupId: r.groupId });
  };

  const testFrom = useCallback(
    (groupId: string) => onTest({ startGroupId: groupId, run: Date.now() }),
    [onTest],
  );

  const focus = useCallback(
    (target: Exclude<IssueTarget, null>) => {
      if (target.kind === 'edge') {
        const edge = doc.edges.find((e) => e.id === target.edgeId);
        setPicked({ edgeId: target.edgeId });
        const ids = [target.groupId, edge?.to_group_id].filter((x): x is string => !!x);
        void flow.fitView({ nodes: ids.map((id) => ({ id })), duration: 400, maxZoom: 1.1, padding: 0.3 });
        return;
      }
      setPicked(
        target.kind === 'block'
          ? { groupId: target.groupId, blockId: target.blockId }
          : { groupId: target.groupId },
      );
      void flow.fitView({ nodes: [{ id: target.groupId }], duration: 400, maxZoom: 1.1, padding: 0.4 });
    },
    [doc, flow],
  );

  // Delete/Backspace apaga o que está selecionado (ligação, bloco ou card) — só com o foco no canvas.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key !== 'Delete' && e.key !== 'Backspace') || !onCanvas(e.target, wrapper.current)) return;
      const edgeId = selectedEdgeId(selection);
      const blockId = selectedBlockId(selection);
      const groupId = selectedGroupId(selection);
      if (!edgeId && !groupId) return;
      e.preventDefault();
      if (edgeId) update((d) => removeEdges(d, [edgeId]));
      else if (blockId) update((d) => deleteBlock(d, blockId));
      else if (groupId) update((d) => deleteGroup(d, groupId));
      setPicked(blockId && groupId ? { groupId } : null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selection, update]);

  const builder = useMemo<Builder>(
    () => ({ doc, issues: index, selection, select, update, addBlock, testFrom }),
    [doc, index, selection, select, update, addBlock, testFrom],
  );

  return (
    <BuilderContext.Provider value={builder}>
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="hidden w-52 shrink-0 bg-surface lg:block">
          <Palette onAdd={addFromPalette} onAddCard={addCard} hasTarget={!!selectedGroupId(selection)} />
        </div>
        <div className="relative min-w-0 flex-1">
          <Canvas wrapper={wrapper} />
        </div>
        <div className={cn('w-[22rem] shrink-0 bg-surface', test && 'invisible')}>
          <SidePanel
            tab={panel}
            onTab={onPanel}
            issues={issues}
            onFocusIssue={focus}
            onCheck={onCheck}
            checking={checking}
          />
        </div>
        {test && (
          <div className="absolute inset-y-0 right-0 z-10 w-[24rem] max-w-full bg-surface shadow-xl">
            <TestPanel
              key={`${test.run}`}
              doc={doc}
              flowName={flowName}
              startGroupId={test.startGroupId}
              onRestart={(startGroupId) => onTest({ startGroupId, run: Date.now() })}
              onClose={() => onTest(null)}
            />
          </div>
        )}
      </div>
    </BuilderContext.Provider>
  );
}
